import Icon from "@react-native-vector-icons/material-design-icons/static";
import type React from "react";
import { useCallback, useEffect } from "react";
import {
	Dimensions,
	Modal,
	Pressable,
	StyleSheet,
	TouchableWithoutFeedback,
} from "react-native";
import type { GestureResponderEvent, StyleProp, ViewStyle } from "react-native";
import { Divider, Text } from "react-native-paper";
import { IconButton } from "react-native-paper";
import type { IconButtonProps } from "react-native-paper";
import Animated, {
	type AnimatedStyle,
	useSharedValue,
	useAnimatedStyle,
	withTiming,
} from "react-native-reanimated";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import Button from "../Button";

const { width: screenWidth } = Dimensions.get("window");

type SideSheetProps = {
	isVisible?: boolean;
	children: React.ReactNode;
	side?: "left" | "right";
	width: number;
	hasBackdrop?: boolean;
	animationDuration?: number;
	layout?: "modal" | "default";
	borderRadiusStyle?: "modal" | "default";
	title?: string;
	containerStyle?: StyleProp<ViewStyle>;
	mainContent?: React.ReactNode;
	hasArrow?: boolean;
	hasDivider?: boolean;
	hasActions?: boolean;
	hasCloseIcon?: boolean;
	backIcon?: IconButtonProps;
	closeIcon?: IconButtonProps;
	primaryAction?: {
		label: string;
		onPress: () => void;
	};
	secondaryAction?: {
		label: string;
		onPress: () => void;
	};
	isResponsive?: boolean;
	onClose?: () => void;
	surfaceColor?: string;
};

type SheetHeaderProps = {
	title?: string;
	hasArrow?: boolean;
	hasCloseIcon?: boolean;
	backIcon?: IconButtonProps;
	onBackPress?: (e: GestureResponderEvent) => void;
	onClosePress?: (e: GestureResponderEvent) => void;
};

const SheetHeader = ({
	title,
	hasArrow,
	hasCloseIcon,
	backIcon,
	onBackPress,
	onClosePress,
}: SheetHeaderProps) => {
	const theme = useAppTheme();
	return (
		<Box paddingHorizontal={24} paddingBottom={16}>
			<Box flexDirection="row" alignItems="center">
				{hasArrow && (
					<IconButton
						icon={backIcon?.icon ?? "arrow-left"}
						onPress={onBackPress}
						size={24}
					/>
				)}
				<Text variant="titleLarge">{title}</Text>
				{hasCloseIcon && (
					<Pressable onPress={onClosePress} style={{ marginLeft: "auto" }}>
						<Icon
							name={"close"}
							size={24}
							color={theme.colors.onSurfaceVariant}
						/>
					</Pressable>
				)}
			</Box>
		</Box>
	);
};

type SheetActionsProps = {
	primaryAction?: {
		label: string;
		onPress: () => void;
	};
	secondaryAction?: {
		label: string;
		onPress: () => void;
	};
};

const SheetActions = ({
	primaryAction,
	secondaryAction,
}: SheetActionsProps) => {
	const theme = useAppTheme();
	const SHOW_ACTIONS = primaryAction || secondaryAction;
	if (!SHOW_ACTIONS) {
		return null;
	}

	return (
		<>
			<Box
				style={{
					height: 1,
					backgroundColor: theme.colors.outlineVariant,
					width: "100%",
				}}
			/>
			<Box paddingHorizontal={24} paddingTop={16} flexDirection="row" gap={8}>
				{primaryAction && (
					<Button
						mode="contained"
						color={theme.colors.primary}
						onPress={primaryAction.onPress}
						style={{ height: 40 }}
					>
						<Text
							style={{ color: theme.colors.onPrimary }}
							variant="labelLarge"
						>
							{primaryAction.label}
						</Text>
					</Button>
				)}
				{secondaryAction && (
					<Button
						mode="outlined"
						color={theme.colors.primary}
						style={{
							height: 40,
							backgroundColor: theme.colors.onPrimary,
						}}
						onPress={secondaryAction.onPress}
					>
						<Text style={{ color: theme.colors.primary }} variant="labelLarge">
							{secondaryAction.label}
						</Text>
					</Button>
				)}
			</Box>
		</>
	);
};

type SheetContentProps = {
	sheetAnimatedStyle: AnimatedStyle<ViewStyle>;
	containerStyle: AnimatedStyle<ViewStyle>;
	responsive: boolean;
	hasDivider: boolean;
	layout: "modal" | "default";
	title?: string;
	hasArrow: boolean;
	hasCloseIcon: boolean;
	backIcon?: IconButtonProps;
	onBackPress: (e: GestureResponderEvent) => void;
	onClosePress: (e: GestureResponderEvent) => void;
	children: React.ReactNode;
	hasActions: boolean;
	primaryAction?: SideSheetProps["primaryAction"];
	secondaryAction?: SideSheetProps["secondaryAction"];
	surfaceColor: string;
	outlineVariantColor: string;
};

const SheetContent = ({
	sheetAnimatedStyle,
	containerStyle,
	responsive,
	hasDivider,
	layout,
	title,
	hasArrow,
	hasCloseIcon,
	backIcon,
	onBackPress,
	onClosePress,
	children,
	hasActions,
	primaryAction,
	secondaryAction,
	surfaceColor,
	outlineVariantColor,
}: SheetContentProps) => (
	<>
		<Animated.View
			style={[
				sheetAnimatedStyle,
				styles.sheet,
				responsive && styles.responsiveContainer,
				{ backgroundColor: surfaceColor, paddingTop: 24 },
			]}
		>
			{hasDivider && (
				<Divider
					style={{
						width: 1,
						height: "100%",
						backgroundColor: outlineVariantColor,
					}}
				/>
			)}
			{layout === "default" && (
				<Animated.View
					style={[
						containerStyle,
						styles.defaultSheet,
						{ flexDirection: "row" },
					]}
				>
					<Box flex={1}>
						<SheetHeader
							title={title}
							hasArrow={hasArrow}
							hasCloseIcon={hasCloseIcon}
							backIcon={backIcon}
							onBackPress={onBackPress}
							onClosePress={onClosePress}
						/>
						<Box marginLeft={1} flex={1}>
							{children}
						</Box>
						{hasActions && (
							<SheetActions
								primaryAction={primaryAction}
								secondaryAction={secondaryAction}
							/>
						)}
					</Box>
				</Animated.View>
			)}
			{layout === "modal" && (
				<Box flex={1} minHeight={0} backgroundColor={surfaceColor}>
					<SheetHeader
						title={title}
						hasArrow={hasArrow}
						hasCloseIcon={hasCloseIcon}
						backIcon={backIcon}
						onBackPress={onBackPress}
						onClosePress={onClosePress}
					/>
					<Box flex={1} minHeight={0}>
						{children}
					</Box>
					{hasActions && (
						<SheetActions
							primaryAction={primaryAction}
							secondaryAction={secondaryAction}
						/>
					)}
				</Box>
			)}
		</Animated.View>
	</>
);

const SideSheet = ({
	isVisible: visible = false,
	children,
	side = "right",
	width = screenWidth * 0.85,
	hasBackdrop: backdrop = false,
	animationDuration = 300,
	layout = "default",
	title,
	hasArrow = true,
	hasActions = true,
	hasCloseIcon = true,
	backIcon,
	primaryAction,
	secondaryAction,
	closeIcon,
	hasDivider = false,
	borderRadiusStyle,
	isResponsive: responsive = false,
	onClose,
	surfaceColor,
}: SideSheetProps) => {
	const sheetWidth = useSharedValue(visible ? width : 0);
	const translateX = useSharedValue(side === "right" ? width : -width);
	const backdropOpacity = useSharedValue(visible ? 0.12 : 0);
	const animatedOpacity = useSharedValue(visible ? 1 : 0);
	const animatedContainerOpacity = useSharedValue(visible ? 1 : 0);
	const bodyTranslateX = useSharedValue(0);
	const theme = useAppTheme();

	const containerStyle = useAnimatedStyle(
		() => ({
			opacity: animatedContainerOpacity.value,
		}),
		[animatedContainerOpacity],
	);

	const sheetAnimatedStyle = useAnimatedStyle(() => {
		return layout === "default"
			? {
					width: sheetWidth.value,
					opacity: animatedOpacity.value,
					[side]: 0,
					backgroundColor: theme.colors.surface,
					borderRadius:
						borderRadiusStyle === "modal" && theme.roundness > 0 ? 16 : 0,
				}
			: {
					width,
					opacity: animatedOpacity.value,
					[side]: 0,
					transform: [{ translateX: translateX.value }],
					backgroundColor: theme.colors.surface,
					borderRadius:
						borderRadiusStyle === "modal" && theme.roundness > 0 ? 16 : 0,
				};
	}, [
		layout,
		sheetWidth,
		animatedOpacity,
		side,
		theme.colors.surface,
		width,
		translateX,
	]);

	const backdropAnimatedStyle = useAnimatedStyle(
		() => ({
			opacity: backdropOpacity.value,
			backgroundColor: theme.colors.scrim,
		}),
		[backdropOpacity, theme.colors.scrim],
	);

	const openSheet = useCallback(() => {
		if (layout === "default") {
			sheetWidth.value = withTiming(width, { duration: animationDuration });
			animatedOpacity.value = withTiming(1, { duration: animationDuration });
			animatedContainerOpacity.value = withTiming(1, {
				duration: animationDuration * 2,
			});
			if (responsive) {
				const bodyOffset = side === "right" ? -width : width;
				bodyTranslateX.value = withTiming(bodyOffset, {
					duration: animationDuration,
				});
			}
		} else {
			translateX.value = withTiming(0, { duration: animationDuration });
			animatedOpacity.value = withTiming(1, { duration: animationDuration });
		}
		if (backdrop) {
			backdropOpacity.value = withTiming(0.12, { duration: animationDuration });
		}
	}, [
		layout,
		width,
		animationDuration,
		responsive,
		side,
		backdrop,
		animatedOpacity,
		bodyTranslateX,
		translateX,
		backdropOpacity,
		sheetWidth,
		animatedContainerOpacity,
	]);

	const closeSheet = useCallback(() => {
		if (layout === "default") {
			sheetWidth.value = withTiming(0, { duration: animationDuration });
			animatedOpacity.value = withTiming(0, { duration: animationDuration });
			animatedContainerOpacity.value = withTiming(0, {
				duration: animationDuration / 2,
			});
			if (responsive) {
				bodyTranslateX.value = withTiming(0, { duration: animationDuration });
			}
		} else {
			translateX.value = withTiming(side === "right" ? width : -width, {
				duration: animationDuration,
			});
			animatedOpacity.value = withTiming(0, { duration: animationDuration });
		}
		if (backdrop) {
			backdropOpacity.value = withTiming(0, { duration: animationDuration });
		}
	}, [
		layout,
		width,
		animationDuration,
		responsive,
		side,
		backdrop,
		animatedOpacity,
		bodyTranslateX,
		translateX,
		backdropOpacity,
		sheetWidth,
		animatedContainerOpacity,
	]);

	const requestClose = useCallback(
		(e?: GestureResponderEvent) => {
			closeSheet();
			if (e) {
				closeIcon?.onPress?.(e);
			}
			onClose?.();
		},
		[closeIcon, closeSheet, onClose],
	);

	useEffect(() => {
		if (visible) {
			openSheet();
		} else {
			closeSheet();
		}
	}, [visible, openSheet, closeSheet]);

	const sheetContent = (
		<SheetContent
			sheetAnimatedStyle={sheetAnimatedStyle}
			containerStyle={containerStyle}
			responsive={responsive}
			hasDivider={hasDivider}
			layout={layout}
			title={title}
			hasArrow={hasArrow}
			hasCloseIcon={hasCloseIcon}
			backIcon={backIcon}
			onBackPress={(e) => backIcon?.onPress?.(e)}
			onClosePress={(e) => {
				requestClose(e);
			}}
			hasActions={hasActions}
			primaryAction={primaryAction}
			secondaryAction={secondaryAction}
			surfaceColor={surfaceColor ?? theme.colors.background}
			outlineVariantColor={theme.colors.outlineVariant}
		>
			{children}
		</SheetContent>
	);

	if (layout === "default") {
		return sheetContent;
	}

	return (
		<Modal
			visible={visible}
			transparent
			animationType="none"
			onRequestClose={requestClose}
		>
			<Box style={styles.modalRoot}>
				{backdrop && (
					<TouchableWithoutFeedback onPress={requestClose}>
						<Animated.View style={[styles.backdrop, backdropAnimatedStyle]} />
					</TouchableWithoutFeedback>
				)}
				{sheetContent}
			</Box>
		</Modal>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		flexDirection: "row",
	},
	responsiveContainer: {
		position: "relative",
	},
	modalRoot: {
		...StyleSheet.absoluteFill,
		zIndex: 9999,
	},
	backdrop: {
		...StyleSheet.absoluteFill,
		zIndex: 1,
	},
	sheet: {
		position: "absolute",
		height: "100%",
		top: 0,
		bottom: 0,
		zIndex: 2,
	},
	defaultSheet: {
		position: "absolute",
		top: 0,
		bottom: 0,
		zIndex: 1000,
	},
	bodyContent: {
		flex: 1,
		backgroundColor: "transparent",
	},
});

export default SideSheet;
